import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import CameraCapture from "./CameraCapture";
import PhotoGallery, { photoWhen } from "./PhotoGallery";

/**
 * MATERIAL PHOTOS — the same records everywhere: the Install Material view, the Delivery view, the
 * FRS picking ticket view and View All Onsite Material all open this.
 *
 * A photo belongs to the MATERIAL RECORDS it documents and carries the location it was taken at as
 * a stamp, so it travels with the material and still reads "Taken at Level 1 Corridor · Sep 16".
 * Pass `placementId` for the finest level (one product at one location, from the detail dialog);
 * otherwise pass the room line's `frsItemKeys` and the location label — the server resolves the
 * records standing there, which is what makes a bulk drop show on all fifty lines from one image.
 */
export default function MaterialPhotos({
  projectId, locationLabel, locationType = "", roomKey = "", roomLabel = "",
  frsItemKeys = [], placementId = "", frsItemKey = "", productShort = "", ticketId = "",
  actor = "", role = "", onClose, onChanged,
}) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [camera, setCamera] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/material/photos", { params: {
        project_id: projectId, location_label: locationLabel, placement_id: placementId,
        frs_item_keys: frsItemKeys.join(","),
      } });
      setPhotos(data.photos || []);
    } catch {
      setPhotos([]);
    }
    setLoading(false);
  }, [projectId, locationLabel, placementId, frsItemKeys.join(",")]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("project_id", projectId);
    fd.append("location_label", locationLabel);
    fd.append("location_type", locationType);
    fd.append("room_key", roomKey);
    fd.append("room_label", roomLabel);
    if (placementId) fd.append("placement_id", placementId);
    if (frsItemKey) fd.append("frs_item_key", frsItemKey);
    if (productShort) fd.append("product_short", productShort);
    if (ticketId) fd.append("ticket_id", ticketId);
    fd.append("uploader", actor || "");
    fd.append("file", file);
    const { data } = await api.post("/material/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const remove = async (p) => {
    try {
      await api.delete(`/material/photos/${p.id}`, { params: { actor, role } });
      toast.success("Photo deleted", {
        description: (p.placement_ids?.length || 0) > 1
          ? "It is gone from every line that showed it" : "" });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete that photo");
    }
  };

  return (
    <>
      <PhotoGallery
        open onClose={onClose} testid="material-photos"
        title={`Material photos · ${locationLabel}`}
        subtitle={placementId
          ? `${productShort || "this product"} — its own photo history, wherever it has been`
          : roomLabel
            ? `The material ${roomLabel} holds at ${locationLabel} — newest first, earlier locations read as history beneath`
            : `The material at ${locationLabel} — newest first, earlier locations read as history beneath`}
        photos={photos} loading={loading}
        onCapture={() => setCamera(true)}
        onDelete={remove}
        metaOf={(p) => `Taken at ${p.location_label || "—"} · ${photoWhen(p.uploaded_at)}`}
        emptyText="No photos of this material yet"
      />
      {camera && (
        <CameraCapture open upload={upload} title={`Photos · ${locationLabel}`}
                       onDrop={(rec) => api.delete(`/material/photos/${rec.id}`, { params: { actor, role } })}
                       onClose={() => setCamera(false)}
                       onDone={async () => { await load(); onChanged?.(); }} />
      )}
    </>
  );
}
